// src/utils/asyncQueue.js
class AsyncQueue {
  constructor() {
    this.queue = [] // 任务队列
    this.isProcessing = false // 是否正在处理任务（类私有属性）
    this.onStatusChange = null // 状态变更回调（供组件监听）
  }

  /**
   * 添加任务到队列
   * @param {Function} task 异步任务函数（返回Promise）
   * @returns {Promise} 任务执行结果
   */
  enqueue(task) {
    return new Promise((resolve, reject) => {
      this.queue.push({ task, resolve, reject })
      this.processNext() // 尝试执行下一个任务
    })
  }

  /**
   * 执行队列中的下一个任务
   */
  async processNext() {
    if (this.isProcessing || this.queue.length === 0) return

    // 标记为执行中，并通知组件
    this.setProcessing(true)

    const { task, resolve, reject } = this.queue.shift() // 取出第一个任务

    try {
      const result = await task() // 执行任务
      resolve(result)
    } catch (error) {
      reject(error)
      console.error('队列任务执行失败:', error)
    } finally {
      // 执行完成，标记为空闲，并通知组件
      this.setProcessing(false)
      this.processNext() // 执行下一个任务
    }
  }

  /**
   * 设置执行状态，并触发回调通知组件
   * @param {boolean} status 执行状态（true-执行中，false-空闲）
   */
  setProcessing(status) {
    this.isProcessing = status
    // 触发状态变更回调，让组件同步状态
    if (typeof this.onStatusChange === 'function') {
      this.onStatusChange(status, this.queue.length)
    }
  }

  /**
   * 清空队列
   */
  clear() {
    this.queue = []
    // 清空队列后如果没有执行中任务，通知组件状态
    if (!this.isProcessing) {
      this.setProcessing(false)
    }
  }

  /**
   * 获取队列长度
   */
  getLength() {
    return this.queue.length
  }

  /**
   * 获取当前执行状态（供组件访问）
   */
  getProcessingStatus() {
    return this.isProcessing
  }
}

export default AsyncQueue
